import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixBookingsV1Category from './bookings-v1-category-categories.http.js';

/** Categories are used to group multiple services together. A service must be associated with a category in order to be exposed in the Wix Bookings UI. */
export interface Category {
  /**
   * Category ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Category name.
   * @maxLength 500
   */
  name?: string | null;
  /**
   * @internal
   * @internal
   * @readonly
   */
  status?: StatusWithLiterals;
  /**
   * @internal
   * @internal */
  sortOrder?: number | null;
}

export enum Status {
  /** The category was created. */
  CREATED = 'CREATED',
  /** The category was deleted. */
  DELETED = 'DELETED',
}

/** @enumType */
export type StatusWithLiterals = Status | 'CREATED' | 'DELETED';

export interface ListCategoryRequest {
  /**
   * IDs of the categories to retrieve.
   *
   * Default: All categories are retrieved.
   * @format GUID
   */
  categoryIds?: string[];
  /**
   * @internal
   * @internal */
  includeDeleted?: boolean | null;
}

export interface ListCategoryResponse {
  /** Retrieved categories. */
  categories?: Category[];
}

/** An event sent every time a category entity is changed. */
export interface CategoryNotification {
  category?: Category;
  event?: EventWithLiterals;
}

export enum Event {
  /** Category was updated. */
  Updated = 'Updated',
  /** Category was deleted. */
  Deleted = 'Deleted',
  /** Category was created. */
  Created = 'Created',
}

/** @enumType */
export type EventWithLiterals = Event | 'Updated' | 'Deleted' | 'Created';

export interface CreateCategoryRequest {
  /** Category to create. */
  category: Category;
}

export interface CreateCategoryResponse {
  /** Created category. */
  category?: Category;
}

export interface BatchCreateCategoryRequest {
  /** Categories to create. */
  categories?: Category[];
}

export interface BatchCreateCategoryResponse {
  /** Created categories. */
  categories?: Category[];
}

export interface UpdateCategoryRequest {
  /** Category to update. */
  category: Category;
}

export interface UpdateCategoryResponse {
  /** Updated category. */
  category?: Category;
}

export interface DeleteCategoryRequest {
  /**
   * ID of the category to delete.
   * @format GUID
   */
  _id: string | null;
  /**
   * Whether to delete all the services associated with the category.
   *
   * Default: `false`
   */
  deleteServices?: boolean | null;
}

export interface DeleteCategoryResponse {
  /**
   * ID of the deleted category.
   * @format GUID
   */
  _id?: string | null;
}

export interface BatchDeleteCategoryRequest {
  /**
   * IDs of the categories to delete.
   * @format GUID
   */
  ids?: string[] | null;
}

export interface BatchDeleteCategoryResponse {}

export interface BatchUpdateCategoryRequest {
  /** List of categories to be updated. */
  categories?: Category[];
  /** Field mask of fields to update. */
  fieldMask?: string[];
}

export interface BatchUpdateCategoryResponse {}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type UpdateCategoryApplicationErrors = {
  code?: 'NOT_FOUND';
  description?: string;
  data?: Record<string, any>;
};

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface CategoryNotificationEnvelope {
  data: CategoryNotification;
  metadata: BaseEventMetadata;
}

/** @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Read Bookings - Public Data
 * @permissionScopeId SCOPE.DC-BOOKINGS.READ-BOOKINGS-PUBLIC
 * @permissionScope Manage Bookings Services and Settings
 * @permissionScopeId SCOPE.BOOKINGS.CONFIGURATION
 * @permissionScope Wix Multilingual - Nile Wrapper Domain Events Read
 * @permissionScopeId SCOPE.MULTILINGUAL.NILE_WRAPPER_DOMAIN_EVENTS_READ
 * @permissionScope Manage Events
 * @permissionScopeId SCOPE.EVENTS.MANAGE-EVENTS
 * @permissionScope Manage Bookings
 * @permissionScopeId SCOPE.DC-BOOKINGS.MANAGE-BOOKINGS
 * @permissionScope Read Bookings - Including Participants
 * @permissionScopeId SCOPE.DC-BOOKINGS.READ-BOOKINGS-SENSITIVE
 * @permissionScope Read Bookings - all read permissions
 * @permissionScopeId SCOPE.DC-BOOKINGS-MEGA.READ-BOOKINGS
 * @permissionScope Manage Bookings - all permissions
 * @permissionScopeId SCOPE.DC-BOOKINGS-MEGA.MANAGE-BOOKINGS
 * @permissionId BOOKINGS.CATEGORIES_READ
 * @webhook
 * @eventType com.wixpress.bookings.services.api.v1.CategoryNotification
 * @serviceIdentifier com.wixpress.bookings.services.api.v1.CategoriesService
 * @slug category_notification
 * @documentationMaturity preview
 * @deprecated
 */
export declare function onCategoryNotification(
  handler: (event: CategoryNotificationEnvelope) => void | Promise<void>
): void;

/**
 * Retrieves all categories.
 * @public
 * @documentationMaturity preview
 * @param options - Options to use when listing categories.
 * @permissionId BOOKINGS.CATEGORIES_READ
 * @applicableIdentity APP
 * @fqn com.wixpress.bookings.services.api.v1.CategoriesService.List
 * @deprecated
 */
export async function listCategories(
  options?: ListCategoriesOptions
): Promise<
  NonNullablePaths<
    ListCategoryResponse,
    `categories` | `categories.${number}.status`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    categoryIds: options?.categoryIds,
    includeDeleted: options?.includeDeleted,
  });

  const reqOpts = ambassadorWixBookingsV1Category.list(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          categoryIds: '$[0].categoryIds',
          includeDeleted: '$[0].includeDeleted',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ListCategoriesOptions {
  /**
   * IDs of the categories to retrieve.
   *
   * Default: All categories are retrieved.
   * @format GUID
   */
  categoryIds?: string[];
  /** @internal */
  includeDeleted?: boolean | null;
}

/**
 * Creates a category.
 * @param category - Category to create.
 * @public
 * @documentationMaturity preview
 * @requiredField category
 * @requiredField category.name
 * @permissionId BOOKINGS.MANAGE_CATEGORIES
 * @applicableIdentity APP
 * @returns Created category.
 * @fqn com.wixpress.bookings.services.api.v1.CategoriesService.Create
 * @deprecated
 */
export async function createCategory(
  category: NonNullablePaths<Category, `name`, 2>
): Promise<NonNullablePaths<Category, `status`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ category: category });

  const reqOpts = ambassadorWixBookingsV1Category.create(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.category!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { category: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['category']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Updates a category.
 *
 *
 * Each time the category is updated, revision increments by 1. You must include
 * the number of the existing revision when updating the category. This ensures
 * you're working with the latest service information and prevents unintended
 * overwrites.
 * @param _id - Category ID.
 * @public
 * @documentationMaturity preview
 * @requiredField _id
 * @requiredField category
 * @requiredField category.name
 * @permissionId BOOKINGS.MANAGE_CATEGORIES
 * @applicableIdentity APP
 * @fqn com.wixpress.bookings.services.api.v1.CategoriesService.Update
 * @deprecated
 */
export async function updateCategory(
  _id: string,
  category: NonNullablePaths<UpdateCategory, `name`, 2>
): Promise<
  NonNullablePaths<UpdateCategoryResponse, `category.status`, 3> & {
    __applicationErrorsType?: UpdateCategoryApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    category: { ...category, id: _id },
  });

  const reqOpts = ambassadorWixBookingsV1Category.update(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { category: '$[1]' },
        explicitPathsToArguments: { 'category.id': '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id', 'category']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateCategory {
  /**
   * Category ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Category name.
   * @maxLength 500
   */
  name?: string | null;
  /**
   * @internal
   * @readonly
   */
  status?: StatusWithLiterals;
  /** @internal */
  sortOrder?: number | null;
}

/**
 * Deletes a category.
 *
 *
 * You can specify `{"deleteServices": true}` to also delete all associated
 * services. Learn more about [deleting a service](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/delete-service).
 * @param _id - ID of the category to delete.
 * @public
 * @documentationMaturity preview
 * @requiredField _id
 * @permissionId BOOKINGS.MANAGE_CATEGORIES
 * @applicableIdentity APP
 * @fqn com.wixpress.bookings.services.api.v1.CategoriesService._delete
 * @deprecated
 */
export async function deleteCategory(
  _id: string,
  options?: DeleteCategoryOptions
): Promise<DeleteCategoryResponse> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    id: _id,
    deleteServices: options?.deleteServices,
  });

  const reqOpts = ambassadorWixBookingsV1Category._delete(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          id: '$[0]',
          deleteServices: '$[1].deleteServices',
        },
        singleArgumentUnchanged: false,
      },
      ['_id', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface DeleteCategoryOptions {
  /**
   * Whether to delete all the services associated with the category.
   *
   * Default: `false`
   */
  deleteServices?: boolean | null;
}
